/**
 * useCalendar Hook
 * Manages calendar and meeting operations with Firebase
 * Automatically generates Jitsi meeting URLs on confirmation
 * Sends email notifications via send-meeting-email edge function
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Meeting, MeetingFormData, MeetingStatus, CalendarEvent } from '@/types/calendar.types';
import { useToast } from '@/hooks/use-toast';
import { generateJitsiMeetingUrl } from '@/utils/jitsi';
import { invokeFunctionSilent } from '@/lib/cloudrun';
import { limit } from 'firebase/firestore';

export function useCalendar(dealId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch meetings
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let q;
    if (dealId) {
      q = query(
        collection(db, 'meetings'),
        where('dealId', '==', dealId)
      );
    } else {
      // Avoid composite index requirement by not combining array-contains with orderBy
      q = query(
        collection(db, 'meetings'),
        where('participants', 'array-contains', user.id)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const meetingsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate(),
        confirmedAt: doc.data().confirmedAt?.toDate(),
        completedAt: doc.data().completedAt?.toDate(),
      })) as Meeting[];
      // Sort client-side since we removed orderBy to avoid composite index
      meetingsData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setMeetings(meetingsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching meetings:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id, dealId]);

  // Send meeting notification email via edge function
  const sendMeetingEmail = useCallback(async (
    contactEmail: string,
    contactFirstName: string,
    contactRole: string,
    eventType: 'confirmation' | 'cancelled' | 'rescheduled',
    meetingTitle: string,
    scheduledAt: string,
    duration: string,
    meetingUrl?: string,
  ) => {
    try {
      await invokeFunctionSilent('send-meeting-email', {
email: contactEmail,
firstName: contactFirstName,
role: contactRole,
eventType,
language: 'en',
meetingTitle,
scheduledAt,
duration,
meetingLink: meetingUrl,
});
    } catch (error) {
      console.error('Failed to send meeting email:', error);
    }
  }, []);

  // Check if email belongs to a platform member
  const checkIsMember = useCallback(async (email: string): Promise<boolean> => {
    try {
      const snap = await getDocs(
query(collection(db, 'users'), where('email', '==', email), limit(1))
);
return !snap.empty;
    } catch {
      return false;
    }
  }, []);

  // Schedule new meeting
  const scheduleMeeting = useCallback(async (data: MeetingFormData): Promise<string | null> => {
    if (!user?.id) return null;

    try {
      const participants = [user.id, ...data.participants.filter(p => p !== user.id)];
      
      // Auto-generate Jitsi URL for video meetings at creation
      const meetingUrl = data.format === 'video' 
        ? generateJitsiMeetingUrl(undefined, data.title) 
        : undefined;

      const meetingData = {
        ...data,
        contacts: data.contacts || (data.contact ? [data.contact] : []),
        organizerId: user.id,
        participants,
        status: 'pending' as MeetingStatus,
        createdAt: Timestamp.now(),
        ...(meetingUrl && { meetingUrl }),
      };

      const docRef = await addDoc(collection(db, 'meetings'), meetingData);

      // Create notification for all participants
      for (const participantId of participants) {
        if (participantId !== user.id) {
          await addDoc(collection(db, 'userNotifications'), {
            userId: participantId,
            type: 'meeting_scheduled',
            title: 'New Meeting Scheduled',
            message: `You have been invited to: ${data.title}`,
            relatedMeetingId: docRef.id,
            relatedDealId: data.dealId,
            createdAt: Timestamp.now(),
            seen: false,
          });
        }
      }

      // Send email notification to contact
      if (data.contact?.email) {
        const scheduledAtStr = `${data.date} ${data.time}`;
        const durationStr = `${data.duration} min`;
        
        await sendMeetingEmail(
          data.contact.email,
          data.contact.firstName,
          data.contact.role || 'external',
          'confirmation',
          data.title,
          scheduledAtStr,
          durationStr,
          meetingUrl,
        );

        // If member, also send internal notification
        const isMember = await checkIsMember(data.contact.email);
        if (isMember) {
          // Internal notification is already handled above via participants
          console.log('Contact is a platform member - internal notification sent');
        }
      }

      toast({
        title: 'Meeting Scheduled',
        description: 'Your meeting has been scheduled successfully.',
      });

      return docRef.id;
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({
        title: 'Error',
        description: 'Failed to schedule meeting.',
        variant: 'destructive',
      });
      return null;
    }
  }, [user?.id, toast, sendMeetingEmail, checkIsMember]);

  // Update meeting
  const updateMeeting = useCallback(async (meetingId: string, data: Partial<MeetingFormData>): Promise<boolean> => {
    try {
      await updateDoc(doc(db, 'meetings', meetingId), {
        ...data,
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Meeting Updated',
        description: 'Your meeting has been updated successfully.',
      });

      return true;
    } catch (error) {
      console.error('Error updating meeting:', error);
      toast({
        title: 'Error',
        description: 'Failed to update meeting.',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  // Update meeting status (auto-generates Jitsi link on confirmation)
  const updateMeetingStatus = useCallback(async (meetingId: string, status: MeetingStatus): Promise<boolean> => {
    try {
      const updateData: Record<string, unknown> = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (status === 'confirmed') {
        updateData.confirmedAt = Timestamp.now();
        
        // Auto-generate Jitsi meeting URL if not already set
        const meeting = meetings.find(m => m.id === meetingId);
        if (meeting && !meeting.meetingUrl) {
          updateData.meetingUrl = generateJitsiMeetingUrl(undefined, meeting.title);
        }
      } else if (status === 'completed') {
        updateData.completedAt = Timestamp.now();
      }

      await updateDoc(doc(db, 'meetings', meetingId), updateData);

      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        // Notify participants
        for (const participantId of meeting.participants) {
          await addDoc(collection(db, 'userNotifications'), {
            userId: participantId,
            type: `meeting_${status}`,
            title: `Meeting ${status.charAt(0).toUpperCase() + status.slice(1)}`,
            message: `The meeting "${meeting.title}" has been ${status}.${status === 'confirmed' && updateData.meetingUrl ? ' A video conference link has been generated.' : ''}`,
            relatedMeetingId: meetingId,
            relatedDealId: meeting.dealId,
            createdAt: Timestamp.now(),
            seen: false,
          });
        }

        // Send email notification to contact
        if (meeting.contact?.email && (status === 'confirmed' || status === 'cancelled')) {
          const scheduledAtStr = `${meeting.date} ${meeting.time}`;
          const durationStr = `${meeting.duration} min`;
          const meetingUrl = status === 'confirmed' ? (updateData.meetingUrl as string || meeting.meetingUrl) : undefined;

          await sendMeetingEmail(
            meeting.contact.email,
            meeting.contact.firstName,
            meeting.contact.role || 'external',
            status === 'confirmed' ? 'confirmation' : 'cancelled',
            meeting.title,
            scheduledAtStr,
            durationStr,
            meetingUrl,
          );
        }
      }

      toast({
        title: 'Status Updated',
        description: `Meeting has been ${status}.`,
      });

      return true;
    } catch (error) {
      console.error('Error updating meeting status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update meeting status.',
        variant: 'destructive',
      });
      return false;
    }
  }, [meetings, toast, sendMeetingEmail]);

  // Delete meeting
  const deleteMeeting = useCallback(async (meetingId: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, 'meetings', meetingId));

      toast({
        title: 'Meeting Deleted',
        description: 'The meeting has been deleted.',
      });

      return true;
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete meeting.',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  // Get calendar events for display
  const getCalendarEvents = useCallback((): CalendarEvent[] => {
    return meetings.map(meeting => {
      const [hours, minutes] = meeting.time.split(':').map(Number);
      const start = new Date(meeting.date);
      start.setHours(hours, minutes, 0, 0);
      
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + meeting.duration);

      return {
        id: meeting.id,
        title: meeting.title,
        start,
        end,
        type: meeting.type,
        status: meeting.status,
        meeting,
      };
    });
  }, [meetings]);

  // Filter meetings by status
  const getUpcomingMeetings = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return meetings.filter(m => {
      const meetingDate = new Date(m.date + 'T12:00:00');
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate >= today && m.status !== 'cancelled' && m.status !== 'completed';
    });
  }, [meetings]);

  const getPastMeetings = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return meetings.filter(m => {
      const meetingDate = new Date(m.date + 'T12:00:00');
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate < today || m.status === 'completed';
    });
  }, [meetings]);

  return {
    meetings,
    loading,
    scheduleMeeting,
    updateMeeting,
    updateMeetingStatus,
    deleteMeeting,
    getCalendarEvents,
    getUpcomingMeetings,
    getPastMeetings,
  };
}
